import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import CampoTexto from '@/components/base/CampoTexto';
import CampoSenha from './CampoSenha';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type ResultadoCriar =
  | { tipo: 'ok' }
  | { tipo: 'ja-existe' }
  | { tipo: 'erro'; mensagem: string };

interface CriarContaAbaProps {
  onCriar: (nome: string, email: string, senha: string) => Promise<ResultadoCriar>;
}

interface Erros {
  nome?: string;
  email?: string;
  senha?: string;
}

export default function CriarContaAba({ onCriar }: CriarContaAbaProps) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erros, setErros] = useState<Erros>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const nomeLimpo = nome.trim();
    const emailLimpo = email.trim();
    const novosErros: Erros = {};

    if (!nomeLimpo) novosErros.nome = 'Digite seu nome completo.';
    if (!emailLimpo) novosErros.email = 'Digite seu e-mail.';
    else if (!EMAIL_REGEX.test(emailLimpo)) novosErros.email = 'Digite um e-mail válido, como maria@gmail.com';
    if (senha.length < 6) novosErros.senha = 'A senha precisa ter pelo menos 6 caracteres.';

    if (Object.keys(novosErros).length > 0) {
      setErros(novosErros);
      setErroGeral(null);
      return;
    }

    setErros({});
    setErroGeral(null);
    setEnviando(true);
    const resultado = await onCriar(nomeLimpo, emailLimpo, senha);
    setEnviando(false);

    if (resultado.tipo === 'erro') {
      setErroGeral(resultado.mensagem);
    }
    // 'ok' e 'ja-existe' são tratados pelo pai (navegação / troca de aba).
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="animate-fade-in">
      <div className="space-y-4">
        <CampoTexto
          id="criar-nome"
          name="nome"
          label="Seu nome completo"
          help="Como você quer ser chamado no site."
          value={nome}
          onChange={setNome}
          required
          error={erros.nome ?? null}
          autoComplete="name"
        />
        <CampoTexto
          id="criar-email"
          name="email"
          label="Seu e-mail"
          help="Você vai usar este e-mail para entrar. Não enviamos nada para ele."
          value={email}
          onChange={setEmail}
          required
          error={erros.email ?? null}
          type="email"
          inputMode="email"
          autoComplete="email"
        />
        <CampoSenha
          id="criar-senha"
          name="senha"
          label="Crie uma senha"
          help="No mínimo 6 caracteres. Anote em algum lugar seguro."
          value={senha}
          onChange={setSenha}
          required
          error={erros.senha ?? null}
          autoComplete="new-password"
          mostrarForca
        />
      </div>

      {erroGeral && (
        <p className="mt-4 text-sm text-danger-600" role="alert">
          {erroGeral}
        </p>
      )}

      <button
        type="submit"
        disabled={enviando}
        className="mt-6 flex h-12 w-full items-center justify-center rounded-md bg-primary-500 text-[15px] font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-200 disabled:text-secondary-400 whitespace-nowrap"
      >
        {enviando ? 'Criando conta...' : 'Criar conta e continuar'}
      </button>

      <p className="mt-3 text-center text-xs leading-relaxed text-secondary-500">
        Ao criar sua conta, você concorda com os{' '}
        <Link
          to="/termos"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary-600 transition-colors hover:text-primary-700"
        >
          Termos de uso
        </Link>{' '}
        e a{' '}
        <Link
          to="/privacidade"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary-600 transition-colors hover:text-primary-700"
        >
          Política de privacidade
        </Link>
        .
      </p>
    </form>
  );
}